import { useEffect, useMemo, useState } from 'react';
import { useDepsChanged } from './useDepsChanged';

/**
 * Debounce a value.
 *
 * If any dep in `immediateDeps` changes, then the value will be updated immediately.
 */
export function useDebouncedValue<T>(value: T, delay: number, immediateDeps?: unknown[]): T {
  // Based on https://usehooks.com/useDebounce/.
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  const changeImmediate = useDepsChanged(immediateDeps || []);

  useEffect(
    () => {
      if (changeImmediate) {
        setDebouncedValue(value);
      } else {
        // Update debounced value after delay
        const handler = setTimeout(() => {
          setDebouncedValue(value);
        }, delay);

        // Cancel the timeout if value changes (also on delay change or unmount)
        // This is how we prevent debounced value from updating if value is changed
        // within the delay period. Timeout gets cleared and restarted.
        return () => {
          clearTimeout(handler);
        };
      }
    },
    [value, delay, changeImmediate], // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

/**
 * Debounce a function.
 */
export function useDebouncedFunction<T extends (...args: any) => any>(fn: T, delay: number) {
  return useMemo(() => debounce(fn, delay), [delay, fn]);
}

const debounce = <T extends (...args: any[]) => any>(func: T, delay: number) => {
  let timerId: number | undefined;

  return (...args: Parameters<T>) => {
    window.clearTimeout(timerId);
    timerId = window.setTimeout(() => func(...args), delay);
  };
};
